import React, { Suspense, lazy, useEffect, useState } from "react";

// -----------------------------------------------------------------------------
// CinematicBackground — mounts the Three.js scene only when:
//   1. The viewport is at least MIN_WIDTH_PX (desktop / larger tablets)
//   2. The user hasn't set `prefers-reduced-motion: reduce`
//
// Otherwise it renders a static gold-veined midnight-blue gradient poster.
// The heavy scene is loaded via React.lazy so it's split out of the main
// bundle and only fetched when actually needed.
// -----------------------------------------------------------------------------
const MIN_WIDTH_PX = 768;

// Lazy split: React.lazy + a dynamic import means the ~200KB three/r3f bundle
// is fetched only if the visitor's device qualifies for the full experience.
const Scene = lazy(() => import("./SceneCanvas"));

function useCanRender3D() {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const check = () => {
      const wide = window.innerWidth >= MIN_WIDTH_PX;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setOk(wide && !reduce);
    };
    check();
    window.addEventListener("resize", check);
    // matchMedia change events fire when the user toggles OS-level reduce-motion.
    const mm = window.matchMedia("(prefers-reduced-motion: reduce)");
    mm.addEventListener?.("change", check);
    return () => {
      window.removeEventListener("resize", check);
      mm.removeEventListener?.("change", check);
    };
  }, []);
  return ok;
}

function StaticPoster() {
  // Radial-gradient poster with a subtle gold-veined vignette. No canvas, no
  // animation — the mobile / reduced-motion fallback. Still feels premium.
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      data-testid="cinematic-poster"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #1A2F5A 0%, #0F2042 45%, #050B14 100%)",
      }}
    >
      {/* Subtle gold veining via layered radial glows. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 70% 30%, rgba(251, 191, 36, 0.10) 0%, transparent 60%), radial-gradient(ellipse 40% 60% at 20% 80%, rgba(251, 191, 36, 0.06) 0%, transparent 55%)",
        }}
      />
      {/* Faint grain — sells "cinematic" without a video texture. */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />
    </div>
  );
}

export default function CinematicBackground() {
  const canRender3D = useCanRender3D();
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 z-0 overflow-hidden"
      data-testid="cinematic-background"
    >
      {canRender3D ? (
        <Suspense fallback={<StaticPoster />}>
          <Scene />
        </Suspense>
      ) : (
        <StaticPoster />
      )}
      {/* Scrim overlay — pushes contrast up for hero text readability. Sits
          above the canvas but below any hero content (z-20). */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,32,66,0.15) 0%, rgba(15,32,66,0.55) 55%, rgba(15,32,66,0.92) 100%)",
        }}
      />
    </div>
  );
}
